import React from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import find from 'lodash/find'
import Icon from '../components/misc/Icons'
import { updatePreviewItem } from '../actions/previewItem'
import { showSectionSelectionModal } from '../actions/modals'
import StaticQuestionSlide from '../components/slides/StaticQuestionSlide'
import { openItemInEditor } from '../utils/openItemInEditor'
import { trackPreviewEvent } from '../services/search/newSearchAnalytics'

// Classify the previewed item for analytics ('set' / 'question' / null).
function previewItemTypeOf(set, question) {
  if (set) return 'set'
  if (question) return 'question'
  return null
}

// set/question preview modal opened by pressing space bar when have item selected in library/recent,
// or by pressing Enter on a highlighted set/question in search (dropdown + results page).

// Action row sitting at the bottom of the modal. Open / Play / Enqueue / Edit /
// close are clickable, each showing its label followed by a keyboard-key hint.
// For sets, prev/next slide buttons are pinned to the left/right ends of the
// row and disabled at the boundaries. Built as its own subcomponent so future
// actions slot in.
function ItemPreviewModalShortcutHelp({
  showNav, slideWidth, onOpen, onPlay, onEnqueue, onEdit, onClose,
  onPrev, onNext, prevDisabled, nextDisabled,
}) {
  return (
    <div style={{ width: `${slideWidth}px` }} className='itemPreviewModal-shortcutHelp'>
      {showNav && (
        <button
          type='button'
          className={`itemPreviewModal-shortcutHelp-action${prevDisabled ? ' itemPreviewModal-shortcutHelp-action--disabled' : ''}`}
          onClick={onPrev}
          onMouseDown={(e) => { e.preventDefault() }}
          disabled={prevDisabled}
        >
          <span>Previous</span>
          <kbd className='itemPreviewModal-shortcutHelp-kbd--arrow'>&larr;</kbd>
        </button>
      )}
      <button type='button' className='itemPreviewModal-shortcutHelp-action' onClick={onOpen} onMouseDown={(e) => { e.preventDefault() }}>
        <span>Open</span>
        <kbd>Enter</kbd>
      </button>
      <button type='button' className='itemPreviewModal-shortcutHelp-action' onClick={onPlay} onMouseDown={(e) => { e.preventDefault() }}>
        <span>Play Now</span>
        <kbd>P</kbd>
      </button>
      <button type='button' className='itemPreviewModal-shortcutHelp-action' onClick={onEnqueue} onMouseDown={(e) => { e.preventDefault() }}>
        <span>Add to Queue</span>
        <kbd>Q</kbd>
      </button>
      <button type='button' className='itemPreviewModal-shortcutHelp-action' onClick={onEdit} onMouseDown={(e) => { e.preventDefault() }}>
        <span>Edit</span>
        <kbd>E</kbd>
      </button>
      <button type='button' className='itemPreviewModal-shortcutHelp-action' onClick={onClose} onMouseDown={(e) => { e.preventDefault() }}>
        <span>Close</span>
        <kbd>Esc</kbd>
      </button>
      {showNav && (
        <button
          type='button'
          className={`itemPreviewModal-shortcutHelp-action${nextDisabled ? ' itemPreviewModal-shortcutHelp-action--disabled' : ''}`}
          onClick={onNext}
          onMouseDown={(e) => { e.preventDefault() }}
          disabled={nextDisabled}
        >
          <span>Next</span>
          <kbd className='itemPreviewModal-shortcutHelp-kbd--arrow'>&rarr;</kbd>
        </button>
      )}
    </div>
  )
}

class ItemReviewModalSetCounterItem extends React.Component {
  render() {
    const {
      index, isCurrentQuestion, question, setPreviewQuestionIndex,
    } = this.props
    return (
      <div
        key={index}
        onClick={() => { setPreviewQuestionIndex(index) }}
        className={`itemPreviewModal-setCounter-item ${
          isCurrentQuestion ? ' itemPreviewModal-setCounter-item--activeQuestion ' : ' itemPreviewModal-setCounter-item--notActiveQuestion '}`}
      >
        <div className='itemPreviewModal-setCounter-item-pillContainer'>
          <div className='itemPreviewModal-setCounter-item-pill' />
        </div>

        <div className='itemPreviewModal-setCounter-item-slideContainer'>
          <StaticQuestionSlide
            question={question}
            stage='ask'
            mode='view'
            displayWidth={320}
            previewCard
          />
        </div>
      </div>
    )
  }
}

class ItemReviewModalSetCounter extends React.Component {
  render() {
    const { set, previewQuestionIndex } = this.props
    return (
      <div className='itemPreviewModal-setCounter'>
        {set.questions.map((question, index) => (
          <ItemReviewModalSetCounterItem
            key={index}
            index={index}
            isCurrentQuestion={index === previewQuestionIndex}
            question={question}
            setPreviewQuestionIndex={this.props.setPreviewQuestionIndex}
          />
        ))}
      </div>
    )
  }
}

class ItemReviewModal extends React.Component {
  constructor(props) {
    super(props)
    this.handleKeyDown = this.handleKeyDown.bind(this)
  }

  UNSAFE_componentWillMount() {
    document.body.style.overflowY = 'hidden' // prevent scrolling while modal is open
    document.addEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown(e) {
    if (e.keyCode === 32) { // space key
      e.preventDefault()
      this.props.updatePreviewItem(null)
    }
  }

  componentWillUnmount() {
    document.body.style.overflowY = '' // overlay is not supported by firefox, so default to blank (auto style comes from css)
    document.body.style.overflowY = 'overlay' // overlay is to prevent windows scrollbars interacting with pagewidth
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  render() {
    const {
      set, previewQuestionIndex, question, onOpen, onPlay, onEnqueue, onEdit, onClose, slideWidth,
    } = this.props
    return (
      // preventDefault on mousedown anywhere in the card keeps focus on the
      // top-nav search input, so a dropdown-launched preview doesn't collapse
      // the dropdown behind it when the slide/card is clicked.
      <div
        className={`itemPreviewModal ${question ? 'itemPreviewModal--question' : 'itemPreviewModal--set'}`}
        onMouseDown={(e) => { e.preventDefault() }}
      >
        {set && (
        <button className={`itemPreviewModal-navigateBtn itemPreviewModal-navigateBtn--previous ${previewQuestionIndex === 0 ? 'itemPreviewModal-navigateBtn--unavailable' : ''}`} onClick={this.props.previousArrowOnClick} onMouseDown={(e) => { e.preventDefault() }}>
          <div className='itemPreviewModal-navigateBtn-iconContainer'>
            <Icon name='chevron-left' />
          </div>
        </button>
        )}

        {set && (
        <button className={`itemPreviewModal-navigateBtn itemPreviewModal-navigateBtn--next${(previewQuestionIndex + 1) === set.questions.length ? 'itemPreviewModal-navigateBtn--unavailable' : ''}`} onClick={this.props.nextArrowOnClick} onMouseDown={(e) => { e.preventDefault() }}>
          <div className='itemPreviewModal-navigateBtn-iconContainer'>
            <Icon name='chevron-right' />
          </div>
        </button>
        )}

        {set && (
        <div style={{ width: `${this.props.slideWidth}px` }} className='itemPreviewModal-topBar'>
          <div className='itemPreviewModal-topBar-counter' />
          <div className='itemPreviewModal-topBar-setTitle'>
            {set.name}
          </div>
          <div className='itemPreviewModal-topBar-counter'>
            {previewQuestionIndex + 1}
            {' '}
            /
            {set.questions.length}
          </div>
        </div>
        )}

        <div style={{ width: `${this.props.slideWidth}px`, height: `${this.props.slideWidth / 1.6}px` }} className='itemPreviewModal-slideContainer'>

          <StaticQuestionSlide
            question={question || set.questions[previewQuestionIndex]}
            displayWidth={this.props.slideWidth}
            stage='ask'
            mode='view'
            previewCard
            showCorrect
            mediaIsPlayable
            key={previewQuestionIndex}
          />

        </div>

        {set && (
        <div className='itemPreviewModal-setCounterContainer'>
          <ItemReviewModalSetCounter
            setPreviewQuestionIndex={this.props.setPreviewQuestionIndex}
            set={set}
            previewQuestionIndex={previewQuestionIndex}
          />
        </div>
        )}

        <ItemPreviewModalShortcutHelp
          showNav={!!set}
          slideWidth={slideWidth}
          onOpen={onOpen}
          onPlay={onPlay}
          onEnqueue={onEnqueue}
          onEdit={onEdit}
          onClose={onClose}
          onPrev={this.props.previousArrowOnClick}
          onNext={this.props.nextArrowOnClick}
          prevDisabled={!!set && previewQuestionIndex === 0}
          nextDisabled={!!set && (previewQuestionIndex + 1) === set.questions.length}
        />
      </div>
    )
  }
}

class ItemPreviewModalContainer extends React.Component {
  constructor(props) {
    super(props)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.previousArrowOnClick = this.previousArrowOnClick.bind(this)
    this.nextArrowOnClick = this.nextArrowOnClick.bind(this)
    this.handleBackgroundClick = this.handleBackgroundClick.bind(this)
    this.openSectionSelection = this.openSectionSelection.bind(this)
    this.openEditor = this.openEditor.bind(this)
    this.openItem = this.openItem.bind(this)
    this.closePreview = this.closePreview.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.resizeRaf = null
    this.state = {
      previewQuestionIndex: 0,
      // Bumped on window resize to re-run the slideWidth computation in render.
      viewportTick: 0,
    }
  }

  UNSAFE_componentWillMount() {
    // Capture phase + swallow-everything (see handleKeyDown): while the modal is
    // open it fully owns the keyboard, so the still-focused search input and its
    // dropdown behind the modal stay frozen at the same position.
    document.addEventListener('keydown', this.handleKeyDown, true)
    // Recompute the slide size when the window resizes so the preview never
    // overflows or under-fills the viewport (slideWidth is derived from
    // window.innerWidth/innerHeight in render).
    window.addEventListener('resize', this.handleResize)
  }

  // Throttle resize re-renders to one per animation frame.
  handleResize() {
    if (this.resizeRaf) return
    this.resizeRaf = window.requestAnimationFrame(() => {
      this.resizeRaf = null
      this.setState((s) => ({ viewportTick: s.viewportTick + 1 }))
    })
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.previewItem !== this.props.previewItem) {
      this.setState({ previewQuestionIndex: 0 })
      // Preview opened (transition from nothing → a previewed item): emit a
      // usage event tagged with where it was launched from.
      if (!this.props.previewItem && nextProps.previewItem) {
        trackPreviewEvent('Opened', {
          source: nextProps.previewItemSource || null,
          itemType: previewItemTypeOf(nextProps.set, nextProps.question),
          itemId: nextProps.previewItem,
        })
      }
    }
  }

  // Shared flat attributes for every preview-action analytics event.
  previewEventProps() {
    const { set, question, previewItemSource } = this.props
    const item = set || question
    return {
      source: previewItemSource || null,
      itemType: previewItemTypeOf(set, question),
      itemId: item ? item.id : null,
    }
  }

  // Close the preview while recording how it was dismissed (Esc/Space key,
  // background click, or the Close button). Open/Edit/Play/Queue paths emit
  // their own event and intentionally do not route through here.
  closePreview(method) {
    trackPreviewEvent('Close', { ...this.previewEventProps(), method })
    this.props.updatePreviewItem(null)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, true)
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeRaf) {
      window.cancelAnimationFrame(this.resizeRaf)
      this.resizeRaf = null
    }
  }

  handleKeyDown(e) {
    const { set, question } = this.props
    // The container is mounted app-wide, but the modal is only on screen when a
    // set/question is being previewed. Bail otherwise so these shortcut keys
    // (←→ / Esc / P / Q / E) don't get captured while the user types elsewhere.
    if (!set && !question) return
    // When the Play/Queue picker is stacked on top, it owns the keyboard — stand
    // down so its own capture-phase handler gets the keys (arrows/Enter/Esc).
    if (this.props.isSectionSelectionModalOpen) return
    // Modal owns the keyboard: stop every keydown from reaching the focused
    // search input / autosuggest behind it (which would scroll the dropdown,
    // move its highlight, or type into the hidden field).
    e.stopImmediatePropagation()
    // Let real browser/system shortcuts (Cmd+R, etc.) through unchanged.
    if (e.metaKey || e.ctrlKey || e.altKey) return
    // Otherwise prevent the default (e.g. typing into the hidden input) and
    // route the key to the modal's own actions.
    e.preventDefault()
    if (e.keyCode === 37) { // left arrow → previous slide (sets only)
      if (set) {
        trackPreviewEvent('Navigate', { ...this.previewEventProps(), direction: 'previous', via: 'key' })
        this.setState((s) => ({ previewQuestionIndex: Math.max(s.previewQuestionIndex - 1, 0) }))
      }
    } else if (e.keyCode === 39) { // right arrow → next slide (sets only)
      if (set) {
        trackPreviewEvent('Navigate', { ...this.previewEventProps(), direction: 'next', via: 'key' })
        this.setState((s) => ({
          previewQuestionIndex: Math.min(s.previewQuestionIndex + 1, set.questions.length - 1),
        }))
      }
    } else if (e.keyCode === 27 || e.keyCode === 32) { // escape / space → close
      this.closePreview(e.keyCode === 27 ? 'escKey' : 'spaceKey')
    } else if (e.keyCode === 13) { // enter → open the item
      this.openItem()
    } else if (e.keyCode === 80) { // P → Play Now
      this.openSectionSelection('play')
    } else if (e.keyCode === 81) { // Q → Add to Queue
      this.openSectionSelection('queue')
    } else if (e.keyCode === 69) { // E → open editor
      this.openEditor()
    }
  }

  // Drop focus from the top-nav search input so the (still-focused) search
  // dropdown frozen behind the modal doesn't re-appear once we navigate / open
  // another modal. Only called from the modal's open actions, not from its
  // close paths (Esc/space/background), which intentionally restore the dropdown.
  blurSearchInput() {
    if (typeof document === 'undefined') return
    const input = document.getElementById('topNavSearchBar')
    if (input && typeof input.blur === 'function') input.blur()
  }

  // Stacks the class-picker modal on top of the preview (the preview stays
  // mounted underneath) in the requested mode ('play' starts Live View, 'queue'
  // enqueues). Dismissing the picker (Esc / background / queue-class-selected)
  // returns to the preview; play-class-selected closes the preview from the
  // picker's container as Live View starts.
  openSectionSelection(mode) {
    const item = this.props.set || this.props.question
    if (!item) return
    trackPreviewEvent(mode === 'play' ? 'Play' : 'Queue', this.previewEventProps())
    this.props.showSectionSelectionModal(item, mode)
  }

  // Closes the lightweight preview and navigates to the item (set/question)
  // detail page — the action that plain Enter used to perform in the dropdown.
  openItem() {
    const { set, question } = this.props
    const item = set || question
    if (!item) return
    trackPreviewEvent('Open Item', this.previewEventProps())
    const path = set ? `/set/${item.id}` : `/question/${item.id}`
    this.blurSearchInput()
    this.props.updatePreviewItem(null)
    this.props.history.push(path)
  }

  // Opens the set/question editor in a new tab and closes the preview. We keep
  // the search input focused (no blur) so a dropdown-launched preview leaves
  // the dropdown intact after closing.
  openEditor() {
    const { set, question } = this.props
    const item = set || question
    if (!item) return
    trackPreviewEvent('Edit', this.previewEventProps())
    openItemInEditor(item.id, !!set)
    this.props.updatePreviewItem(null)
  }

  previousArrowOnClick(e) {
    if (this.props.set) {
      e.stopPropagation()
      const currentIndex = this.state.previewQuestionIndex
      const newIndex = Math.max(currentIndex - 1, 0)
      if (newIndex !== currentIndex) {
        trackPreviewEvent('Navigate', { ...this.previewEventProps(), direction: 'previous', via: 'button' })
      }
      this.setState({ previewQuestionIndex: newIndex })
    }
  }

  nextArrowOnClick(e) {
    if (this.props.set) {
      e.stopPropagation()
      const currentIndex = this.state.previewQuestionIndex
      const newIndex = Math.min(currentIndex + 1, this.props.set.questions.length - 1)
      if (newIndex !== currentIndex) {
        trackPreviewEvent('Navigate', { ...this.previewEventProps(), direction: 'next', via: 'button' })
      }
      this.setState({ previewQuestionIndex: newIndex })
    }
  }

  handleBackgroundClick(e) {
    if (e.target === e.currentTarget) this.closePreview('background') // close modal is click outside modal
  }

  render() {
    const { set, question } = this.props
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const sideButtonWidth = 80 // side buttons for going forward and backward
    const minimumSideBuffer = 80 // space between edge of modal and screen
    const minimumVerticalBuffer = 12 // space between edge of modal and screen
    // Fixed chrome stacked vertically with the slide that must fit on-screen
    // alongside it. Varies by item type:
    //   set      → top bar (38px) + set counter (38px)
    //   question → top + bottom padding (20px + 20px)
    const slideChromeHeight = set ? (38 + 38) : (20 + 20)
    // Shortcut / action row below the slide: ~52px for a single line (button
    // height + 7px/13px margins) plus an allowance for it wrapping to a second
    // line on narrow widths, so the modal never grows past the viewport.
    const actionRowHeight = 88
    const availableWidth = Math.min(1280, (screenWidth - sideButtonWidth - sideButtonWidth - minimumSideBuffer - minimumSideBuffer))
    const availableHeight = Math.min(800, (screenHeight - (minimumVerticalBuffer * 2) - slideChromeHeight - actionRowHeight))
    let slideWidth
    if ((availableHeight * 1.6) > availableWidth) {
      // taller than wide
      slideWidth = availableWidth
    } else {
      // wider than tall
      slideWidth = availableHeight * 1.6
    }
    if (set) {
      return (
        <div className='itemPreviewModalContainer'>

          <ItemReviewModal
            updatePreviewItem={this.props.updatePreviewItem}
            previousArrowOnClick={this.previousArrowOnClick}
            nextArrowOnClick={this.nextArrowOnClick}
            set={set}
            previewQuestionIndex={this.state.previewQuestionIndex}
            slideWidth={slideWidth}
            handleBackgroundClick={this.handleBackgroundClick}
            setPreviewQuestionIndex={(index) => {
              if (index !== this.state.previewQuestionIndex) {
                trackPreviewEvent('Navigate', { ...this.previewEventProps(), via: 'counter' })
              }
              this.setState({ previewQuestionIndex: index })
            }}
            onOpen={this.openItem}
            onPlay={() => { this.openSectionSelection('play') }}
            onEnqueue={() => { this.openSectionSelection('queue') }}
            onEdit={this.openEditor}
            onClose={() => { this.closePreview('button') }}
          />

          <div className='itemPreviewModalContainer-bg' onClick={this.handleBackgroundClick} onMouseDown={(e) => { e.preventDefault() }} />

        </div>

      )
    } if (question) {
      return (
        <div className='itemPreviewModalContainer'>

          <ItemReviewModal
            updatePreviewItem={this.props.updatePreviewItem}
            question={question}
            slideWidth={slideWidth}
            handleBackgroundClick={this.handleBackgroundClick}
            onOpen={this.openItem}
            onPlay={() => { this.openSectionSelection('play') }}
            onEnqueue={() => { this.openSectionSelection('queue') }}
            onEdit={this.openEditor}
            onClose={() => { this.closePreview('button') }}
          />

          <div className='itemPreviewModalContainer-bg' onClick={this.handleBackgroundClick} onMouseDown={(e) => { e.preventDefault() }} />

        </div>

      )
    } return null
  }
}

function mapStateToProps(state) {
  const { previewItem } = state
  let set
  let question
  if (previewItem) {
    set = find(state.sets, { id: previewItem })
    question = find(state.questions, { id: previewItem })
  }
  return {
    previewItem,
    previewItemSource: state.previewItemSource,
    set,
    question,
    isSectionSelectionModalOpen: state.modals.currentModal === 'SECTION_SELECTION_MODAL',
  }
}

export default withRouter(connect(
  mapStateToProps,
  { updatePreviewItem, showSectionSelectionModal },
)(ItemPreviewModalContainer))
